/**
 * Acerto proporcional quando o responsável troca de plano no meio do ciclo.
 *
 * O valor da diária é a mensalidade dividida pelas ocorrências que aquele ciclo
 * realmente tinha (4 ou 5 segundas, por exemplo), e não por uma média — é o que
 * o responsável de fato pagou por dia naquele mês.
 */
export type SwitchProrationMode = "credit" | "charge" | "none";

export interface SwitchProration {
  dailyValue: number;
  usedUnits: number;
  /** Negativo credita a primeira cobrança do plano novo; positivo cobra. */
  amount: number;
  mode: SwitchProrationMode;
}

export interface SwitchProrationInput {
  price: number;
  /** Ocorrências dos dias fixos naquele ciclo. Zero = contrato pendente. */
  cycleOccurrences: number;
  usedUnits: number;
  chargePaid: boolean;
}

const toCents = (value: number) => Math.round(value * 100) / 100;

export function computeSwitchProration({
  price,
  cycleOccurrences,
  usedUnits,
  chargePaid,
}: SwitchProrationInput): SwitchProration {
  const used = Math.max(0, Math.min(usedUnits, Math.max(0, cycleOccurrences)));

  if (cycleOccurrences <= 0) {
    return { dailyValue: 0, usedUnits: Math.max(0, usedUnits), amount: 0, mode: "none" };
  }

  const dailyValue = toCents(price / cycleOccurrences);

  if (chargePaid) {
    const credit = toCents(price - dailyValue * used);
    return { dailyValue, usedUnits: used, amount: credit > 0 ? -credit : 0, mode: credit > 0 ? "credit" : "none" };
  }

  const charge = toCents(dailyValue * used);
  return { dailyValue, usedUnits: used, amount: charge, mode: charge > 0 ? "charge" : "none" };
}
